import { useState, createContext, useContext, useEffect } from 'react'
import type { ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from '@cottage/design-system/internal-tooling/ui'
import Sidebar from './components/Sidebar'
import Settings from './pages/Settings'
import EmailRules from './pages/EmailRules'
import DocReview from './pages/DocReview'
import EmailSearch from './pages/EmailSearch'
import BookkeeperLayout from './pages/bookkeeper/BookkeeperLayout'
import DistributionWaterfallLayout from './pages/distribution-waterfall/DistributionWaterfallLayout'
import HpThresholdLayout from './pages/distribution-waterfall/HpThresholdLayout'
import MemberLedgerLayout from './pages/member-ledger/MemberLedgerLayout'
import UnitLedger from './pages/member-ledger/UnitLedger'
import Calculator from './pages/distribution-waterfall/Calculator'
import WaterfallHistory from './pages/distribution-waterfall/History'
import HpThreshold from './pages/distribution-waterfall/HpThreshold'
import Accruals from './pages/distribution-waterfall/Accruals'
import CapitalSettings from './pages/distribution-waterfall/CapitalSettings'
import AllocationMethodology from './pages/distribution-waterfall/AllocationMethodology'
import Vesting from './pages/member-ledger/Vesting'
import WaterfallSettingsPage from './pages/distribution-waterfall/WaterfallSettings'
import Conventions from './pages/distribution-waterfall/Conventions'
import Projections from './pages/Projections'
import BottomRightNotificationStack from './components/BottomRightNotificationStack'
import LocationBreadcrumbCapture from './components/LocationBreadcrumbCapture'
import RequireAuth from './components/RequireAuth'
import RequirePageAccess from './components/RequirePageAccess'
import Login from './pages/Login'
import AppSettings from './pages/AppSettings'
import VendorDetails from './pages/VendorDetails'
import PreferredEquity from './pages/PreferredEquity'
import UhcAllocations from './pages/UhcAllocations'
import HomebasePayroll from './pages/HomebasePayroll'
import CloverDailyReports from './pages/CloverDailyReports'
import BankRecon from './pages/BankRecon'
import Warehouse from './pages/Warehouse'
import QboTransactionSearch from './pages/QboTransactionSearch'
import GcpCosts from './pages/GcpCosts'
import MonthlyPerformanceLayout, {
  MonthlyPerformanceIndexRedirect,
} from './pages/monthly-performance/MonthlyPerformanceLayout'
import MonthlyPerformanceMembers from './pages/monthly-performance/MonthlyPerformanceMembers'
import MonthlyPerformanceReport from './pages/monthly-performance/MonthlyPerformanceReport'
import MonthlyPerformanceSettings from './pages/monthly-performance/MonthlyPerformanceSettings'
import FixedAssetsLayout from './pages/fixed-assets/FixedAssetsLayout'
import FixedAssetsAllocate from './pages/fixed-assets/FixedAssetsAllocate'
import FixedAssetsSchedules from './pages/fixed-assets/FixedAssetsSchedules'
import PurchasingLayout from './pages/purchasing/PurchasingLayout'
import GiftCardSend from './pages/gift-cards/GiftCardSend'
import { usePendingReviewPoller } from './hooks/usePendingReviewPoller'
import { usePurchasingNavPoller } from './hooks/usePurchasingNavPoller'
import { useFixedAssetsSyncPoller } from './hooks/useFixedAssetsSyncPoller'
import { useFixedAssetsNavStatusPoller } from './hooks/useFixedAssetsNavStatusPoller'
import { useMonthCloseNavStatusPoller } from './hooks/useMonthCloseNavStatusPoller'
import { useWarehouseNavStatusPoller } from './hooks/useWarehouseNavStatusPoller'
import { useCloverNavStatusPoller } from './hooks/useCloverNavStatusPoller'

const SidebarContext = createContext<{
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
}>({
  sidebarOpen: false,
  setSidebarOpen: () => {},
  sidebarCollapsed: false,
  setSidebarCollapsed: () => {},
})

export const useSidebar = () => useContext(SidebarContext)

function getPageTitle(pathname: string): string {
  if (pathname.startsWith('/bookkeeper') || pathname.startsWith('/invoice-minion')) {
    return 'Invoices'
  }
  if (pathname.startsWith('/hp-threshold')) return 'HP Threshold'
  if (pathname.startsWith('/projections')) return 'Projections'
  if (pathname.startsWith('/gcp')) return 'GCP'
  if (pathname.startsWith('/distribution-waterfall')) return 'Distributions & Income'
  if (pathname.startsWith('/member-ledger')) return 'Member Ledger'
  if (pathname.startsWith('/qbo-transaction-search')) return 'Transactions'
  if (pathname.startsWith('/vendor-details')) return 'Vendors'
  if (pathname.startsWith('/clover')) return 'Clover'
  if (pathname.startsWith('/bank-recon')) return 'Bank Recon'
  if (pathname.startsWith('/warehouse')) return 'Warehouse'
  if (pathname.startsWith('/purchasing')) return 'Purchasing'
  if (pathname.startsWith('/gift-cards')) return 'Gift Cards'
  if (pathname.startsWith('/payroll')) return 'Payroll'
  if (pathname.startsWith('/benefits')) return 'Benefits'
  if (pathname.startsWith('/preferred-equity')) return 'Preferred Equity'
  if (pathname.startsWith('/fixed-assets')) return 'Fixed Assets'
  if (pathname.startsWith('/reporting')) return 'Reporting'
  if (pathname === '/settings') return 'Settings'
  return 'BookKeeper'
}

function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={{ pathname: to, search: location.search }} replace />
}

function LegacyInvoiceMinionRedirect() {
  const location = useLocation()
  const suffix = location.pathname.replace(/^\/invoice-minion/, '') || '/doc-review'
  return <Navigate to={`/bookkeeper${suffix}${location.search}`} replace />
}

function AppLayout({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  return (
    <SidebarContext.Provider
      value={{ sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed }}
    >
      <AppLayoutContent>{children}</AppLayoutContent>
    </SidebarContext.Provider>
  )
}

function AppLayoutContent({ children }: { children: ReactNode }) {
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed } = useSidebar()
  const location = useLocation()
  const pageTitle = getPageTitle(location.pathname)

  useEffect(() => {
    document.title =
      pageTitle === 'BookKeeper' ? 'Bookkeeper' : `${pageTitle} · Bookkeeper`
  }, [pageTitle])

  usePendingReviewPoller()
  usePurchasingNavPoller()
  useFixedAssetsSyncPoller()
  useFixedAssetsNavStatusPoller()
  useWarehouseNavStatusPoller()
  useMonthCloseNavStatusPoller()
  useCloverNavStatusPoller()

  return (
    <AppShell
      className="h-full min-h-0"
      collapsed={sidebarCollapsed}
      sidebar={
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />
      }
      mobileHeader={
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 pb-2.5 pt-[calc(env(safe-area-inset-top,0px)+0.625rem)] flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 p-1"
            aria-label="Open navigation"
          >
            <svg className="block w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-gray-900 leading-6 truncate">{pageTitle}</h1>
          <div className="w-8 shrink-0" aria-hidden />
        </div>
      }
    >
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden pb-[env(safe-area-inset-bottom,0px)]">
        <div className="flex-1 min-h-0 overflow-hidden" data-bk-main>
          <RequirePageAccess>{children}</RequirePageAccess>
        </div>
      </div>

      <LocationBreadcrumbCapture />
      <BottomRightNotificationStack />
    </AppShell>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppLayout>
              <Routes>
                <Route path="/bookkeeper" element={<BookkeeperLayout />}>
                  <Route index element={<Navigate to="doc-review" replace />} />
                  <Route path="doc-review" element={<DocReview />} />
                  <Route path="email-rules" element={<EmailRules />} />
                  <Route path="email-search" element={<EmailSearch />} />
                  <Route path="settings" element={<Settings />} />
                </Route>

                <Route path="/member-ledger" element={<MemberLedgerLayout />}>
                  <Route index element={<Navigate to="unit-ledger" replace />} />
                  <Route path="unit-ledger" element={<UnitLedger />} />
                  <Route path="vesting" element={<Vesting />} />
                </Route>

                <Route path="/distribution-waterfall" element={<DistributionWaterfallLayout />}>
                  <Route index element={<Navigate to="calculator" replace />} />
                  <Route path="calculator" element={<Calculator />} />
                  <Route path="history" element={<WaterfallHistory />} />
                  <Route path="accruals" element={<Accruals />} />
                  <Route path="methodology" element={<AllocationMethodology />} />
                  <Route path="settings" element={<CapitalSettings />} />
                </Route>

                <Route path="/projections" element={<Projections />} />
                <Route path="/projections/*" element={<Navigate to="/projections" replace />} />

                <Route
                  path="/distribution-waterfall/hp-threshold"
                  element={<Navigate to="/hp-threshold/balances" replace />}
                />
                <Route
                  path="/distribution-waterfall/conventions"
                  element={<Navigate to="/hp-threshold/conventions" replace />}
                />

                <Route path="/hp-threshold" element={<HpThresholdLayout />}>
                  <Route index element={<Navigate to="balances" replace />} />
                  <Route path="balances" element={<HpThreshold />} />
                  <Route path="settings" element={<WaterfallSettingsPage />} />
                  <Route path="conventions" element={<Conventions />} />
                </Route>

                <Route path="/invoice-minion/*" element={<LegacyInvoiceMinionRedirect />} />

                <Route path="/settings" element={<AppSettings />} />

                <Route path="/vendor-details" element={<VendorDetails />} />
                <Route path="/preferred-equity" element={<PreferredEquity />} />

                <Route path="/fixed-assets" element={<FixedAssetsLayout />}>
                  <Route index element={<Navigate to="allocate" replace />} />
                  <Route path="allocate" element={<FixedAssetsAllocate />} />
                  <Route path="schedules" element={<FixedAssetsSchedules />} />
                </Route>

                <Route path="/purchasing/*" element={<PurchasingLayout />} />

                <Route path="/benefits" element={<UhcAllocations />} />
                <Route path="/payroll" element={<HomebasePayroll />} />
                <Route path="/clover" element={<CloverDailyReports />} />
                <Route path="/gift-cards" element={<GiftCardSend />} />
                <Route path="/bank-recon" element={<BankRecon />} />
                <Route path="/warehouse" element={<Warehouse />} />
                <Route path="/qbo-transaction-search" element={<QboTransactionSearch />} />
                <Route path="/gcp" element={<GcpCosts />} />

                <Route path="/reporting/members" element={<MonthlyPerformanceMembers />} />
                <Route path="/reporting" element={<MonthlyPerformanceLayout />}>
                  <Route index element={<MonthlyPerformanceIndexRedirect />} />
                  <Route path="report" element={<MonthlyPerformanceReport />} />
                  <Route path="settings" element={<MonthlyPerformanceSettings />} />
                </Route>

                <Route
                  path="/doc-review"
                  element={<RedirectWithSearch to="/bookkeeper/doc-review" />}
                />
                <Route
                  path="/email-rules"
                  element={<RedirectWithSearch to="/bookkeeper/email-rules" />}
                />
                <Route
                  path="/email-search"
                  element={<RedirectWithSearch to="/bookkeeper/email-search" />}
                />
                <Route path="/" element={<Navigate to="/bookkeeper/doc-review" replace />} />
                <Route path="*" element={<Navigate to="/bookkeeper/doc-review" replace />} />
              </Routes>
            </AppLayout>
          </RequireAuth>
        }
      />
    </Routes>
  )
}

export default App
